import React, { useCallback, useMemo } from "react";

import { Divider, Grid } from "@mui/material";
import Fade from "@mui/material/Fade";
import { ModalProps } from "@mui/material/Modal";
import { useQuery } from "@tanstack/react-query";
import { useAppState } from "Context/StateContext";
import { AccessCustomerPortal, LocationCustomerPortal } from "Models/customer.portal";
import { useTranslation } from "react-i18next";
import AccessService from "Services/access.service";

import {
  CustomerPortalModalBackdrop,
  CustomerPortalModalCardContent,
} from "../../../../CustomerPortalModal";
import { Modal, ModalCard, ModalTitle, ModalListItem } from "./styled";

interface SummaryCheckListModalProps {
  title?: string;
  access?: AccessCustomerPortal;
  addresses?: LocationCustomerPortal[];
}
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 1 * 60 * 60 * 1000; // 1 hour

export const SummaryCheckListModal = (
  props: SummaryCheckListModalProps & Omit<ModalProps, "children">
) => {
  const { open, title, access, addresses, onClose, ...rest } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const renderYesNo = useCallback((render?: boolean | null) => {
    if (render === true) return translate("yes");
    if (render === false) return translate("no");
  }, []);

  const isValid = useCallback((value?: any) => {
    return value !== null && value !== undefined;
  }, []);

  const { data: carryOptions } = useQuery(
    ["carryDistance"],
    () =>
      AccessService.getAllCarryDistances({
        companyId: currentUser?.companyId,
      }).then(({ data }) => data),
    {
      enabled: open && !!access,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
    }
  );

  const { data: drivewayLengthOptions } = useQuery(
    ["drivewayLength"],
    () =>
      AccessService.getAllDrivewayLengths({
        companyId: currentUser?.companyId,
      }).then(({ data }) => {
        return data;
      }),
    {
      enabled: open && !!access,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
    }
  );

  const drivewayLength = useMemo(() => {
    if (access?.driveway?.length || !drivewayLengthOptions) return;
    const find = drivewayLengthOptions.find((option) => option.value === access?.driveway?.length);

    return find?.name;
  }, [access, drivewayLengthOptions]);

  const carryDistance = useMemo(() => {
    if (!access?.carryDistance || !carryOptions) return;

    const find = carryOptions.find((option) => option.value === access.carryDistance);

    return find?.name;
  }, [access, carryOptions]);

  const streetAccess = useMemo(() => {
    if (!access?.streetsAccesses?.length) return;

    return access.streetsAccesses.map((streetAccess) => streetAccess?.name).join(", ");
  }, [access]);

  const treesPowerline = useMemo(() => {
    if (access?.treeOrPowerlineOverhanging && access.treeOrPowerlineLow) return "Overhanging / Low";
    if (access?.treeOrPowerlineOverhanging) return "Overhanging";
    if (access?.treeOrPowerlineLow) return "Low";
  }, [access]);

  const liftValue = useMemo(() => {
    if (!isValid(access?.lift)) return;

    if (access!.lift) return translate("yes");

    return translate("no");
  }, [access]);

  const renderAddresses = () => {
    if (!addresses) return;

    return (
      <>
        {addresses?.map((address, index) => {
          return <span key={index}>{address.longAddress}</span>;
        })}
      </>
    );
  };

  const renderStepsValue = () => {
    if (!access?.steps) return;

    if (access.steps.toLowerCase() !== "no") {
      const accessType = access?.stepType ? ` (${access?.stepType})` : "";

      return `${renderYesNo(true)}${accessType}`;
    }

    return renderYesNo(false);
  };

  const renderStepInternalText = () => {
    if (!access?.steps || !access?.stepType) return;

    if (["Both", "Internal"].includes(access.stepType)) {
      return access.stepsInternal ? `Steps ${access.stepsInternal}` : renderYesNo(true);
    }

    return renderYesNo(false);
  };

  const renderStepExternalText = () => {
    if (!access?.steps || !access?.stepType) return;

    if (["Both", "External"].includes(access.stepType)) {
      return access.stepsExternal ? `Steps ${access.stepsExternal}` : renderYesNo(true);
    }

    return renderYesNo(false);
  };

  const renderResidenceTypeText = () => {
    if (!access?.residence) return;

    if (access.residence.type && access.residence.level) {
      return (
        <>
          {access.residence.type}
          <br />
          {access.residence.level}
        </>
      );
    }

    return access.residence.type;
  };

  const renderModalContent = () => {
    if (!access) return;

    return (
      <>
        <ModalTitle hasCloseButton onClick={(event) => onClose?.(event, "escapeKeyDown")}>
          {title}
        </ModalTitle>

        <CustomerPortalModalCardContent>
          <ModalListItem
            title={translate("address")}
            subtitle={renderAddresses() || "-"}
            capitalizeSubtitle
          />

          <Divider />

          <ModalListItem
            title={translate("willTheEntryOfTheHouseBeObstructed")}
            subtitle={renderYesNo(access.entryHouseObstructed) || "-"}
            capitalizeSubtitle
          />

          <Divider />

          <Grid container>
            <Grid item xs={6}>
              <ModalListItem
                title={translate("driveway")}
                subtitle={drivewayLength || "-"}
                capitalizeSubtitle
              />
            </Grid>

            <Grid item xs={6}>
              <ModalListItem
                title={translate("whatIsTheLengthM")}
                subtitle={access?.driveway?.length || "-"}
                capitalizeSubtitle
              />
            </Grid>
          </Grid>

          <Divider />

          <ModalListItem
            title={translate("residenceType")}
            subtitle={renderResidenceTypeText() || "-"}
            capitalizeSubtitle
          />

          <Divider />

          <Grid container>
            <Grid item xs={3}>
              <ModalListItem
                title={translate("steps")}
                subtitle={renderStepsValue() || "-"}
                capitalizeSubtitle
              />
            </Grid>

            <Grid item xs={3}>
              <ModalListItem
                title={translate("hasLift")}
                subtitle={liftValue || "-"}
                capitalizeSubtitle
              />
            </Grid>

            <Grid item xs={3}>
              <ModalListItem
                title={translate("external")}
                subtitle={renderStepExternalText() || "-"}
                capitalizeSubtitle
              />
            </Grid>

            <Grid item xs={3}>
              <ModalListItem
                title={translate("internal")}
                subtitle={renderStepInternalText() || "-"}
                capitalizeSubtitle
              />
            </Grid>
          </Grid>

          <Divider />

          <ModalListItem
            title={translate("streetAccess")}
            subtitle={streetAccess || "-"}
            capitalizeSubtitle
          />

          <Divider />

          <Grid container>
            <Grid item xs={6}>
              <ModalListItem
                title={translate("parking")}
                subtitle={access.parking || "-"}
                capitalizeSubtitle
              />
            </Grid>

            <Grid item xs={6}>
              <ModalListItem
                title={translate("carry")}
                subtitle={carryDistance || "-"}
                capitalizeSubtitle
              />
            </Grid>
          </Grid>
          <Divider />

          <Grid container>
            <Grid item xs={6}>
              <ModalListItem
                title={translate("treesPowerline")}
                subtitle={treesPowerline || "-"}
                capitalizeSubtitle
              />
            </Grid>

            <Grid item xs={6}>
              <ModalListItem
                title={translate("action")}
                subtitle={access.treeOrPowerlineAction || "-"}
                capitalizeSubtitle
              />
            </Grid>
          </Grid>
          <Divider />

          <Grid container>
            <Grid item xs={6}>
              <ModalListItem
                title={translate("vehicleSize")}
                subtitle={access.vehicle || "-"}
                capitalizeSubtitle
              />
            </Grid>

            <Grid item xs={6}>
              <ModalListItem
                title={translate("isFerryRequired")}
                subtitle={renderYesNo(access.ferryRequired) || "-"}
                capitalizeSubtitle
              />
            </Grid>
          </Grid>

          <Divider />

          <ModalListItem
            title={translate("comments")}
            subtitle={access.comments || "-"}
            capitalizeSubtitle
          />
        </CustomerPortalModalCardContent>
      </>
    );
  };

  return (
    <Modal
      open={open && !!access}
      slots={{ backdrop: CustomerPortalModalBackdrop }}
      closeAfterTransition
      disableRestoreFocus
      onClose={onClose}
      {...rest}
    >
      <Fade in={open && !!access && !!addresses && !!title}>
        <ModalCard>{renderModalContent()}</ModalCard>
      </Fade>
    </Modal>
  );
};
